import React, { useContext, useMemo } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { Card, Col, Flex, Progress, Row, theme as antdTheme } from 'antd';

import ProjectChart from '@totem/components/ticketing/details/project/projectChart';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { ticketStyles } from '@totem/components/ticketing/details/ticketStyles';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';

const styles = {
  chartTable: {
    paddingBottom: '32px',
    fontWeight: 'bold',
  },
  chartTableContent: {
    maxWidth: '300px',
    margin: '0 auto',
    backgroundColor: 'lightblue',
  },
};

const ProjectSummary = () => {
  const { useToken } = antdTheme;
  const { token } = useToken();
  const { ticket } = useContext(TicketContext);

  const budgetedHours = useMemo(() => {
    if (isNotNull(ticket) && isNotNull(ticket.ticket.tasks)) {
      const budgeted = ticket.ticket.tasks
        .filter((item) => !item.isDeleted)
        .reduce((accumulator, record) => {
          return accumulator + record.budgetedHours;
        }, 0);
      return budgeted;
    }
    return 0;
  }, [ticket.ticket.tasks]);

  const actualHours = useMemo(() => {
    return isNotNull(ticket) && isNotNull(ticket.ticket.timeEntries)
      ? ticket.ticket.timeEntries.reduce(
          (acc, item) => acc + item.hoursLogged,
          0,
        )
      : 0;
  }, [ticket.ticket?.timeEntries]);

  const completedTask = useMemo(() => {
    return isNotNull(ticket) && isNotNull(ticket.ticket.tasks)
      ? ticket.ticket.tasks.filter(
          (item) =>
            item.type === 'Task' &&
            !item.isDeleted &&
            (item.status === 'COMPLETED' || item.status === 'CANCELLED'),
        ).length
      : 0;
  }, [ticket.ticket?.tasks]);

  const inProgressTask = useMemo(() => {
    return isNotNull(ticket) && isNotNull(ticket.ticket.tasks)
      ? ticket.ticket.tasks.filter(
          (item) =>
            item.type === 'Task' &&
            !item.isDeleted &&
            (item.status === 'IN PROGRESS' || item.status === 'REVIEW'),
        ).length
      : 0;
  }, [ticket.ticket?.tasks]);

  const allTask = useMemo(() => {
    return isNotNull(ticket) && isNotNull(ticket.ticket.tasks)
      ? ticket.ticket.tasks.filter(
          (item) => item.type === 'Task' && !item.isDeleted,
        ).length
      : 0;
  }, [ticket.ticket?.tasks]);

  const effortWeight = useMemo(() => {
    const budgetToActual =
      budgetedHours > 0 ? (actualHours / budgetedHours) * 100 : 0;
    const effort = budgetToActual * 0.4;
    return effort;
  }, [budgetedHours, actualHours]);

  const taskWeight = useMemo(() => {
    const taskCompletion = allTask > 0 ? (completedTask / allTask) * 100 : 0;
    const task = taskCompletion * 0.6;
    return task;
  }, [completedTask, allTask]);

  const weightedPercentage = useMemo(() => {
    return effortWeight + taskWeight;
  }, [effortWeight, taskWeight]);

  return (
    <div>
      <Row gutter={32}>
        <Col span={8}>
          <Card title={'Budget to Actual Hours'}>
            <div style={styles.chartTable}>
              <Flex justify={'space-between'} align={'center'}>
                <div
                  style={{
                    fontSize: token.fontSizeXL,
                    color: ticketStyles.colors.negative,
                  }}
                >
                  {budgetedHours === 0 && 'No Budgeted Hours'}
                  {budgetedHours !== 0 &&
                    `${((actualHours / budgetedHours) * 100).toFixed(1)}% Used`}
                </div>
                <div
                  style={{ textAlign: 'right' }}
                >{`${actualHours.toFixed(1)} of ${budgetedHours.toFixed(1)} hours used`}</div>
              </Flex>
            </div>
            <Flex justify="center" align="middle">
              <Progress
                percent={
                  budgetedHours !== 0 ? (actualHours / budgetedHours) * 100 : 0
                }
                format={(percent) => `${percent.toFixed(1)}%`}
                percentPosition={{ align: 'start', type: 'inner' }}
                size={['100%', 30]}
                railColor={ticketStyles.colors.positive}
                strokeColor={ticketStyles.colors.negative}
              />
            </Flex>
            <div
              style={{
                ...styles.chartTable,
                marginTop: '1em',
                fontSize: token.fontSizeXL,
              }}
            >
              <Flex gap={8} align="flex-start">
                <ClockCircleOutlined
                  style={{
                    marginTop: '3px',
                    color: ticketStyles.colors.negative,
                  }}
                />
                <div>
                  Remaining Hours
                  <br />
                  <span style={{ color: ticketStyles.colors.positive }}>
                    {(budgetedHours - actualHours).toFixed(1)}
                  </span>{' '}
                  Hours
                </div>
              </Flex>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card title={'Task Completion'}>
            <div style={{ ...styles.chartTable, fontSize: token.fontSizeXL }}>
              <Flex justify={'space-between'} align={'center'}>
                <div style={{ fontWeight: 'normal' }}>Completed</div>
                <div
                  style={{
                    textAlign: 'right',
                    color: ticketStyles.colors.positive,
                  }}
                >
                  {completedTask}
                </div>
              </Flex>
              <Flex justify={'space-between'} align={'center'}>
                <div style={{ fontWeight: 'normal' }}>In Progress</div>
                <div
                  style={{
                    textAlign: 'right',
                    color: colors.event.criticality.background.info,
                  }}
                >
                  {inProgressTask}
                </div>
              </Flex>
              <Flex justify={'space-between'} align={'center'}>
                <div style={{ fontWeight: 'normal' }}>Not Started</div>
                <div
                  style={{
                    textAlign: 'right',
                    color: ticketStyles.colors.negative,
                  }}
                >
                  {allTask - (completedTask + inProgressTask)}
                </div>
              </Flex>
            </div>
            <Flex justify="center" align="middle">
              <ProjectChart
                title={''}
                colors={[
                  ticketStyles.colors.positive,
                  colors.event.criticality.background.info,
                  ticketStyles.colors.negative,
                ]}
                data={[
                  { label: 'Completed', value: completedTask },
                  { label: 'In Progress', value: inProgressTask },
                  {
                    label: 'Not Started',
                    value: allTask - (completedTask + inProgressTask),
                  },
                ]}
                totalDisplay={
                  allTask > 0
                    ? `${((completedTask / allTask) * 100).toFixed(1)}%`
                    : ''
                }
              />
            </Flex>
          </Card>
        </Col>
        <Col span={8}>
          <Card title={'Completion Percentage (Weight Average)'}>
            <div style={{ ...styles.chartTable, fontSize: token.fontSizeXL }}>
              <Flex justify={'space-between'} align={'center'}>
                <div style={{ fontWeight: 'normal' }}>Effort Weight 40%</div>
                <div
                  style={{
                    textAlign: 'right',
                    color: ticketStyles.colors.positive,
                  }}
                >
                  {effortWeight.toFixed(1)}%
                </div>
              </Flex>
              <Flex justify={'space-between'} align={'center'}>
                <div style={{ fontWeight: 'normal' }}>Task Weight 60%</div>
                <div
                  style={{
                    textAlign: 'right',
                    color: ticketStyles.colors.positive,
                  }}
                >
                  {taskWeight.toFixed(1)}%
                </div>
              </Flex>
              <Flex justify={'space-between'} align={'center'}>
                <div style={{ fontWeight: 'normal' }}>&nbsp;</div>
                <div
                  style={{
                    textAlign: 'right',
                    color: ticketStyles.colors.negative,
                  }}
                >
                  &nbsp;
                </div>
              </Flex>
            </div>
            <Flex justify="center" align="middle">
              <ProjectChart
                title={''}
                data={[
                  { label: 'Percent Complete', value: weightedPercentage },
                  { label: 'Remaining', value: 100 - weightedPercentage },
                ]}
                totalDisplay={`${weightedPercentage.toFixed(1)}%`}
              />
            </Flex>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProjectSummary;
